import { altLabel, modLabel } from "../lib/platform";

/** A two-column row: what to type or press, and what it does. */
export interface GuideRow {
  input: string;
  result: string;
}

export interface GuideSection {
  title: string;
  /** One or two sentences shown above the rows. */
  intro: string;
  rows: GuideRow[];
  /** Whether `input` is a key chord (rendered as keys) or text to type (rendered as code). */
  kind: "keys" | "text";
}

/**
 * The quick editing guide behind the header's Help button: the handful of things a person
 * needs on day one, in the order they need them. The exhaustive shortcut table lives in the
 * "Keyboard shortcuts" tab (`shortcuts/catalog.ts`); this is deliberately the short version
 * and it must stay short. Bindings are the ones `docs/spec/editor.md` §7 and §10 pin.
 */
export function editingGuide(): GuideSection[] {
  const mod = modLabel();
  const alt = altLabel();
  return [
    {
      title: "Format as you type",
      intro:
        "At the start of a line, type the marker and then a space (␣) and it converts in place; nothing happens until the space. Backspace right after a conversion gives you the literal characters back.",
      kind: "text",
      rows: [
        { input: "#␣", result: "Heading 1; ##␣, ###␣ and ####␣ for smaller headings" },
        { input: "-␣", result: "Bulleted list" },
        { input: "1.␣", result: "Numbered list" },
        { input: "[]␣", result: "Todo checkbox" },
        { input: ">␣", result: "Quote" },
        { input: "```js␣", result: "Code block, with an optional language name" },
        { input: "---", result: "Divider (no space needed)" },
        { input: ":::␣", result: "Info notice" },
        { input: "+++␣", result: "Collapsible toggle block" },
        { input: "|--␣", result: "Table" },
        {
          input: "**bold**",
          result: "Bold as you close it; *italic*, ~~strike~~, `code`, ==highlight==",
        },
        { input: "[text](url)", result: "Link" },
      ],
    },
    {
      title: "Slash menu",
      intro:
        "Type / anywhere and keep typing to filter. Arrow keys move, Enter inserts, Escape closes.",
      kind: "text",
      rows: [
        { input: "/h2", result: "Headings of every size" },
        { input: "/todo", result: "Todo, bulleted and numbered lists" },
        { input: "/image", result: "Image, video, PDF and file attachments (uploads)" },
        { input: "/table", result: "Table, quote, code block, math block" },
        { input: "/date", result: "Today's date, divider, page break, toggle block" },
      ],
    },
    {
      title: "Select text",
      intro:
        "Highlight any text and a toolbar appears above it: heading levels, quote, lists, bold, italic, strikethrough, highlight colours, code, link, comment and copy. Inside a table it also merges and splits cells.",
      kind: "text",
      rows: [],
    },
    {
      title: "Mentions and links",
      intro: "",
      kind: "text",
      rows: [
        { input: "@", result: "Mention a person or link to a document by name" },
        { input: "Paste a URL over selected text", result: "Turns the selection into a link" },
        { input: "Paste markdown", result: "Converts to formatting" },
      ],
    },
    {
      title: "Handy keys",
      intro: "The full list, with a filter, is under Keyboard shortcuts.",
      kind: "keys",
      rows: [
        { input: "Ctrl+Shift+1 … 4", result: "Heading 1 to 4" },
        { input: "Ctrl+Shift+0", result: "Back to a paragraph" },
        { input: `${mod}+B or ${mod}+I`, result: "Bold, italic" },
        { input: `${mod}+E`, result: "Inline code" },
        { input: `${mod}+K`, result: "Add a link" },
        { input: `${mod}+${alt}+M`, result: "Comment on the selection" },
        { input: "Ctrl+Shift+7 … 9", result: "Todo, bulleted, numbered list" },
        { input: "Tab or Shift+Tab", result: "Indent or outdent a list item" },
        { input: `${mod}+${alt}+↑ or ${mod}+${alt}+↓`, result: "Move a block up or down" },
        { input: "Shift+Enter", result: "Line break without a new paragraph" },
      ],
    },
    {
      title: "Good to know",
      intro:
        "Hover the left edge of a block for a handle to drag it elsewhere. Smart text fires while typing (-> becomes an arrow, ... an ellipsis, quotes curl); Backspace reverts it. In a table, Tab moves to the next cell and adds a row at the end. Everything saves continuously and is stored as plain markdown, which is what the API returns.",
      kind: "text",
      rows: [],
    },
  ];
}
